import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Spinner } from "theme-ui";
import {
  approveProviderInvoice,
  getProviderInvoiceReviewContext,
  PoLineInput,
  PoLineKind,
  ReviewContext,
} from "api/ProviderInvoice";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import { themevals } from "theme/themevals";

/**
 * Approve panel (2026-10-20). The reviewer sees the PO next to the invoice,
 * corrects it (quantities, prices, fuel surcharge, sales tax, card fees),
 * and sees what is already on the Zoho bill (deposits / payments) before
 * approving. A bill with payments is updated in place, never deleted.
 */

type Line = {
  key: string;
  id?: string;
  name: string;
  quantity: string;
  cost: string;
  kind: PoLineKind;
};

const CHARGE_LABEL: Record<string, string> = {
  fuel_surcharge: "Fuel surcharge",
  sales_tax: "Sales tax",
  card_fee: "Credit card fee",
  other: "",
};

const money = (n: number) =>
  `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inputSx: React.CSSProperties = {
  border: `1px solid ${themevals.colors.inputOutline}`,
  borderRadius: "4px",
  padding: "4px 6px",
  fontSize: "13px",
  fontFamily: "Poppins",
  width: "100%",
  background: "white",
};

interface Props {
  invoice: any;
  invoiceNumber: string;
  invoiceAmount: number;
  extraOptions: { invoiceNumber?: string; amount?: number };
  onClose: () => void;
  onApproved: (response: any) => void;
  onPreview: () => void;
}

const ApprovePanel: React.FC<Props> = ({ invoice, invoiceNumber, invoiceAmount, extraOptions, onClose, onApproved, onPreview }) => {
  const [ctx, setCtx] = useState<ReviewContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [dirty, setDirty] = useState(false);
  const [deposit, setDeposit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getProviderInvoiceReviewContext(invoice.id).then((res) => {
      if (!live) return;
      if (!res.success || !res.data) {
        setLoadError(res.error || "Could not load the PO for this invoice.");
        return;
      }
      setCtx(res.data);
      setLines(
        res.data.po.lines
          .filter((l) => l.kind !== "adjustment")
          .map((l) => ({ key: l.id, id: l.id, name: l.name, quantity: String(l.quantity), cost: String(l.cost), kind: l.kind })),
      );
    });
    return () => {
      live = false;
    };
  }, [invoice.id]);

  const priorAdjustment = useMemo(
    () => (ctx?.po.lines || []).filter((l) => l.kind === "adjustment").reduce((s, l) => s + l.quantity * l.cost, 0),
    [ctx],
  );
  const lineTotal = (l: Line) => (Number(l.quantity) || 0) * (Number(l.cost) || 0);
  const newTotal = Math.round(lines.reduce((s, l) => s + lineTotal(l), 0) * 100) / 100;
  const diff = Math.round((invoiceAmount - newTotal) * 100) / 100;
  const badQty = lines.some((l) => !/^\d+$/.test(l.quantity.trim()));
  const badCost = lines.some((l) => l.cost.trim() === "" || isNaN(Number(l.cost)));
  const unnamed = lines.some((l) => l.kind === "other" && !l.name.trim());
  const hasFuel = lines.some((l) => l.kind === "fuel_surcharge");

  const edit = (key: string, patch: Partial<Line>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    setDirty(true);
  };
  const remove = (key: string) => {
    setLines((ls) => ls.filter((l) => l.key !== key));
    setDirty(true);
  };
  const addCharge = (kind: PoLineKind) => {
    setLines((ls) => [
      ...ls,
      { key: `new-${Date.now()}-${ls.length}`, name: CHARGE_LABEL[kind] ?? "", quantity: "1", cost: "", kind },
    ]);
    setDirty(true);
  };

  const bill = ctx?.bill && "total" in ctx.bill ? ctx.bill : null;
  const billUnreadable = !!ctx?.bill && "unreadable" in ctx.bill;

  const pathNote = (() => {
    if (!ctx) return "";
    const n = bill?.billNumber ? `bill ${bill.billNumber}` : "the bill";
    if (deposit) {
      return `The PO stays as it is and ${ctx.path === "create" ? "a bill is created" : `${n} stays`} for the full PO (${money(ctx.po.total)}). Log the deposit in Zoho as a payment on that bill; the final invoice will update the same bill.`;
    }
    switch (ctx.path) {
      case "create":
        return "Approving creates the Zoho bill from the PO below.";
      case "adopt":
        return `Zoho already has ${n} on this PO. Approving links it and updates its lines to match the PO below.`;
      case "replace":
        return `This replaces the earlier invoice: ${n} is deleted and recreated from the PO below. Nothing has been paid on it.`;
      case "update_in_place":
        return `${n.charAt(0).toUpperCase() + n.slice(1)} is updated in place. Payments on it stay, and Zoho will show what is still owed.`;
      default:
        return "";
    }
  })();

  const approve = async () => {
    if (!ctx) return;
    if (!deposit && (badQty || badCost || unnamed)) {
      setError(badQty ? "Quantities must be whole numbers." : badCost ? "Every line needs an amount." : "Name the other charge.");
      return;
    }
    if (!deposit && bill && invoiceAmount + 0.005 < bill.paid) {
      setError(`${money(bill.paid)} is already paid on this bill, more than this invoice. Record a vendor credit in Zoho instead.`);
      return;
    }
    setSaving(true);
    setError(null);
    const poLines: PoLineInput[] | undefined =
      dirty && !deposit
        ? lines.map((l) => ({
            ...(l.id ? { id: l.id } : { kind: l.kind }),
            name: l.name.trim() || undefined,
            quantity: Number(l.quantity),
            cost: Number(l.cost),
          }))
        : undefined;
    const res = await approveProviderInvoice(invoice.id, {
      ...extraOptions,
      approveWithVariance: true,
      deposit: deposit || undefined,
      poLines,
    });
    setSaving(false);
    if (res?.success) onApproved(res);
    else setError(res?.error || "Could not approve the invoice.");
  };

  const headCell = { fontSize: "11px", color: themevals.colors.dkgrey, fontWeight: 600, textTransform: "uppercase" as const };

  return (
    <Modal isOpen toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        Approve invoice {invoiceNumber || invoice.id.slice(0, 8)} · {invoice.providerName || "provider"}
      </ModalHeader>
      <ModalBody>
        {!ctx && !loadError && (
          <Flex sx={{ justifyContent: "center", minHeight: "160px", alignItems: "center" }}>
            <Spinner size={28} />
          </Flex>
        )}
        {loadError && <P color={themevals.colors.danger}>{loadError}</P>}
        {ctx && (
          <Box>
            <Flex sx={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "12px", gap: "12px", flexWrap: "wrap" }}>
              <P fontSize="13px">
                Invoice <strong>{money(invoiceAmount)}</strong> against PO{ctx.po.sequence ? ` GMO-${ctx.po.sequence}` : ""} at{" "}
                <strong>{money(ctx.po.total)}</strong>
              </P>
              <BasicButton scheme="secondaryOutline" onClick={onPreview} styles={{ padding: "2px 10px", fontSize: "12px" }}>
                View invoice
              </BasicButton>
            </Flex>

            {(bill || billUnreadable || ctx.otherInvoices.length > 0) && (
              <Box sx={{ background: themevals.colors.ltgrey, borderRadius: "6px", padding: "10px 12px", marginBottom: "14px" }}>
                <P fontSize="12px" fontWeight="600" marginBottom="6px">
                  Already on this PO
                </P>
                {bill && (
                  <P fontSize="12px" marginBottom="4px">
                    Zoho bill {bill.billNumber || bill.billId}: total {money(bill.total)}
                    {bill.paid > 0 ? (
                      <>
                        , <strong>paid {money(bill.paid)}</strong>, balance {money(bill.balance)}
                      </>
                    ) : (
                      ", nothing paid"
                    )}
                    {bill.payments.length > 0 &&
                      ` (${bill.payments
                        .map((p) => `${money(p.amount)}${p.date ? ` on ${p.date}` : ""}${p.reference ? ` ref ${p.reference}` : ""}`)
                        .join("; ")})`}
                  </P>
                )}
                {billUnreadable && (
                  <P fontSize="12px" color={themevals.colors.danger} marginBottom="4px">
                    Zoho bill {ctx.bill?.billId} could not be read. Approval will stop rather than touch it.
                  </P>
                )}
                {ctx.otherInvoices.map((o) => (
                  <P key={o.id} fontSize="12px" color={themevals.colors.dkgrey}>
                    Invoice {o.invoiceNumber || o.id.slice(0, 8)} · {o.amount != null ? money(o.amount) : "no amount"} ·{" "}
                    {o.status.replace(/_/g, " ")}
                    {o.deposit ? " · deposit" : ""}
                  </P>
                ))}
              </Box>
            )}

            <Flex as="label" sx={{ alignItems: "center", gap: "8px", marginBottom: "12px", cursor: "pointer" }}>
              <input type="checkbox" checked={deposit} onChange={(e) => setDeposit(e.target.checked)} />
              <P fontSize="13px">Bill the full PO: this invoice is a deposit, or the balance left after one</P>
            </Flex>

            {!deposit && (
              <Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 70px 100px 90px 24px", gap: "6px", alignItems: "center" }}>
                  <Box sx={headCell}>PO line</Box>
                  <Box sx={headCell}>Qty</Box>
                  <Box sx={headCell}>Cost</Box>
                  <Box sx={{ ...headCell, textAlign: "right" }}>Total</Box>
                  <Box />
                  {lines.map((l) => (
                    <React.Fragment key={l.key}>
                      {l.kind === "item" ? (
                        <P fontSize="13px">{l.name}</P>
                      ) : (
                        <input
                          style={inputSx}
                          value={l.name}
                          placeholder="Name the charge"
                          onChange={(e) => edit(l.key, { name: e.target.value })}
                        />
                      )}
                      <input
                        style={{ ...inputSx, borderColor: /^\d+$/.test(l.quantity.trim()) ? themevals.colors.inputOutline : themevals.colors.danger }}
                        inputMode="numeric"
                        value={l.quantity}
                        onChange={(e) => edit(l.key, { quantity: e.target.value })}
                      />
                      <input
                        style={inputSx}
                        inputMode="decimal"
                        value={l.cost}
                        placeholder="0.00"
                        onChange={(e) => edit(l.key, { cost: e.target.value })}
                      />
                      <P fontSize="13px" textAlign="right">
                        {money(lineTotal(l))}
                      </P>
                      <Box
                        as="button"
                        onClick={() => remove(l.key)}
                        title="Remove line"
                        sx={{ border: "none", background: "none", cursor: "pointer", color: themevals.colors.dkgrey, fontSize: "16px" }}
                      >
                        ×
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>

                <Flex sx={{ gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                  {!hasFuel && (
                    <BasicButton scheme="secondaryOutline" onClick={() => addCharge("fuel_surcharge")} styles={{ padding: "2px 10px", fontSize: "12px" }}>
                      + Fuel surcharge
                    </BasicButton>
                  )}
                  <BasicButton scheme="secondaryOutline" onClick={() => addCharge("sales_tax")} styles={{ padding: "2px 10px", fontSize: "12px" }}>
                    + Sales tax
                  </BasicButton>
                  <BasicButton scheme="secondaryOutline" onClick={() => addCharge("card_fee")} styles={{ padding: "2px 10px", fontSize: "12px" }}>
                    + Credit card fee
                  </BasicButton>
                  <BasicButton scheme="secondaryOutline" onClick={() => addCharge("other")} styles={{ padding: "2px 10px", fontSize: "12px" }}>
                    + Other charge
                  </BasicButton>
                </Flex>

                <Box sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, marginTop: "12px", paddingTop: "10px" }}>
                  <Flex sx={{ justifyContent: "space-between" }}>
                    <P fontSize="13px">PO after your changes</P>
                    <P fontSize="13px" fontWeight="600">{money(newTotal)}</P>
                  </Flex>
                  <Flex sx={{ justifyContent: "space-between" }}>
                    <P fontSize="13px">Invoice</P>
                    <P fontSize="13px" fontWeight="600">{money(invoiceAmount)}</P>
                  </Flex>
                  <P
                    fontSize="12px"
                    marginTop="6px"
                    color={Math.abs(diff) < 0.01 ? themevals.colors.success : "#856404"}
                  >
                    {Math.abs(diff) < 0.01
                      ? "Matches the invoice."
                      : `${money(Math.abs(diff))} ${diff > 0 ? "more than" : "less than"} the PO. Approving adds the difference to the PO as a "Change order adjustment" line. If the invoice is wrong, reject it instead.`}
                  </P>
                  {Math.abs(priorAdjustment) > 0.005 && (
                    <P fontSize="12px" color={themevals.colors.dkgrey} marginTop="4px">
                      An earlier change order adjustment ({money(priorAdjustment)}) is recalculated on approval.
                    </P>
                  )}
                </Box>
              </Box>
            )}

            <P fontSize="12px" color={themevals.colors.dkgrey} marginTop="12px">
              {pathNote}
            </P>
            {error && (
              <P fontSize="13px" color={themevals.colors.danger} marginTop="8px">
                {error}
              </P>
            )}
          </Box>
        )}
      </ModalBody>
      <ModalFooter>
        <BasicButton scheme="secondaryOutline" onClick={onClose}>
          Cancel
        </BasicButton>
        <BasicButton scheme="primary" onClick={approve} disabled={!ctx || saving || billUnreadable}>
          {saving ? "Approving..." : deposit ? "Approve deposit" : "Approve"}
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default ApprovePanel;
